import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import {
  ButtonSecondary,
  IconChevronDown,
  IconDownload,
  LoadingIndicator,
  PopoverTrigger,
  spacer12,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';
import {
  FilterPopover,
  CounterButton,
} from '@spotify-internal/encore-web/content-platform';
import { useT } from '@spotify-internal/i18n-core';
import { Resource } from '@hooks/useCurrentUserInfo/types';
import { FETCH_STATUS } from '@hooks/useSWRFetch/types';
import { useUnclearedVideosExportRequest } from '@hooks/useUnclearedVideosExportRequest';
import {
  buildAddSnackbarAction,
  SnackBarColorSet,
} from '@context/SnackbarNotifications/actions';
import { useSnackbarNotifications } from '@context/SnackbarNotifications/SnackbarNotifications';
import {
  ButtonContainer,
  LoadingIndicatorContainer,
  StyledWrapper,
  TitleAndFreshnessContainer,
} from '@components/Catalog/ContentArea/CatalogExports/styles';
import { DateRangeDropdown } from '@components/shared/DateRange/DateRangeDropdown';
import { DateRange } from '@components/shared/DateRange/types';
import { MUSIC_VIDEO_US_CLEARANCE_FAQ_URL } from '@helpers/constants';
import { FilterItem } from '@helpers/filterItem';
import { CreateUnclearedVideoExportRequest__Output } from '@generated/grpc/spotify/s4mpview/v1';
import { UnclearedVideosTable } from './UnclearedVideosTable/UnclearedVideosTable';
interface UnclearedVideosProps {
  userLicensors: Resource[];
}

const Container = styled.span`
  margin-right: ${spacer12};
  cursor: pointer;
`;

const InputsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 36px;
  flex-wrap: wrap-reverse;
  flex-direction: row-reverse;
`;

const CounterBtn = styled(CounterButton)`
  span {
    font-size: 10px !important;
    padding: 0 !important;
    margin-left: 5px;
    right: 9px !important;
  }
`;

const DescriptionAndInputsContainer = styled(TitleAndFreshnessContainer)`
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const StyledWrapperLong = styled(StyledWrapper)`
  max-width: 694px;
  margin-bottom: ${spacer8};
`;

const TotalDisplay: React.FC<{ total_number: number }> = ({ total_number }) => {
  const t = useT();
  return (
    <Type variant="celloCanon" paddingBottom={spacer8} as="h1">
      {t(
        'atlas.uncleared_videos_table.title',
        `{total_number, plural,
                    one {1 video}
                    other {{total_number} videos}
            } uncleared`,
        'Title for uncleared videos table indicating total number of uncleared videos',
        { total_number },
      )}
    </Type>
  );
};

export const UnclearedVideos: React.VFC<UnclearedVideosProps> = ({
  userLicensors,
}: UnclearedVideosProps) => {
  const t = useT();
  const [total_number, setTotalNumber] = useState(0);
  const [selectedLicensors, setSelectedLicensors] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  } as DateRange);
  const [searchValue, setSearchValue] = useState<string>('');
  const [showPopover, toggleShowPopover] = useState(false);
  const allLicensorNames: FilterItem[] = userLicensors.map(licensor => ({
    label: licensor.name,
    id: licensor.id,
  }));
  const [filteredLicensors, setFilteredLicensors] =
    useState<FilterItem[]>(allLicensorNames);
  const numSelectedFilters = selectedLicensors.length;
  const licensorNameRef = useRef<HTMLButtonElement>();
  const allUserLicensors = userLicensors.map(licensor => licensor.id);
  const [, dispatchSnackbar] = useSnackbarNotifications();
  const {
    status,
    actions: { s4mpUnclearedExport },
    parseDateRangeAsDateFilter,
    parseLicensorIds,
  } = useUnclearedVideosExportRequest();
  const { locale } = useRouter();

  const handleUnclearedExport = async () => {
    const body: CreateUnclearedVideoExportRequest__Output = {
      licensors: selectedLicensors.length
        ? parseLicensorIds(selectedLicensors)
        : parseLicensorIds(allUserLicensors),
      dateFilter: parseDateRangeAsDateFilter(dateRange),
      languageLocale: locale ? locale : 'en',
      _dateFilter: 'dateFilter',
    };

    await s4mpUnclearedExport(body).then(res => {
      if (!res) {
        dispatchSnackbar(
          buildAddSnackbarAction(
            t(
              'atlas.unassociated_videos_report.snackbars.creating_report_error',
              `We couldn't create your report. Try again.`,
              'Snackbar text that indicates an error occurred with the report',
            ),
            SnackBarColorSet.NEGATIVE,
          ),
        );
      } else {
        dispatchSnackbar(
          buildAddSnackbarAction(
            t(
              'atlas.unassociated_videos_report.snackbars.creating_report',
              `Creating report. You can leave this page. You'll get an email when it's ready.`,
              'Snackbar text that indicates an unassociated video report is being created',
            ),
          ),
        );
      }
    });
  };

  return (
    <>
      <TotalDisplay total_number={total_number} />

      <DescriptionAndInputsContainer>
        <StyledWrapperLong
          dangerouslySetInnerHTML={{
            __html: t(
              'atlas.uncleared_videos_table.description',
              `The rights for the following videos have not been cleared for distribution in some markets. Check the <a href="${MUSIC_VIDEO_US_CLEARANCE_FAQ_URL}" target="_blank">Frequently Asked Questions</a> for more details.`,
              'Text explaining what uncleared videos are and linking to an FAQ',
            ),
          }}
        />
        <InputsContainer>
          <Container>
            <ButtonContainer>
              {status === FETCH_STATUS.FETCHING ? (
                <LoadingIndicatorContainer>
                  <LoadingIndicator indicatorSize="md" />
                </LoadingIndicatorContainer>
              ) : (
                <ButtonSecondary
                  iconLeading={IconDownload}
                  size="small"
                  type="submit"
                  onClick={() => {
                    handleUnclearedExport();
                  }}
                >
                  {t(
                    'atlas.labels.export',
                    'Export',
                    'Button text to create export',
                  )}
                </ButtonSecondary>
              )}
            </ButtonContainer>
          </Container>
          <Container>
            <DateRangeDropdown
              createdAtRange={dateRange}
              onDateRangeChange={setDateRange}
            />
          </Container>
          <Container>
            {userLicensors.length > 1 && (
              <PopoverTrigger
                placement="bottomStart"
                onShow={() => {
                  toggleShowPopover(true);
                }}
                onHide={() => toggleShowPopover(false)}
                overlay={
                  showPopover && (
                    <FilterPopover
                      aria-label={t(
                        'atlas.filter_popover.licensors_aria_label',
                        'Modal to filter licensors',
                        'aria-label attribute for licensors modal',
                      )}
                      clearFilterButtonLabel={t(
                        'atlas.filter_popover.clear',
                        'Clear',
                        'Button to clear current filter selection',
                      )}
                      applyFilterButtonLabel={t(
                        'atlas.filter_popover.apply',
                        'Apply',
                        'Button to apply a new filter selection',
                      )}
                      searchPlaceholder={t(
                        'atlas.search.by_licensor_name',
                        'Search by licensor name',
                        'Directions for users to search by licensor name',
                      )}
                      searchValue={searchValue}
                      name="filter-by-region"
                      items={filteredLicensors}
                      initialSelection={selectedLicensors}
                      onApply={selectedOptions => {
                        setSelectedLicensors(selectedOptions);
                      }}
                      onSearch={input => {
                        const regEx = RegExp(`^${input}`, 'i');
                        setFilteredLicensors(
                          allLicensorNames.filter(item =>
                            item.label.match(regEx),
                          ),
                        );
                        setSearchValue(input);
                      }}
                    />
                  )
                }
              >
                <CounterBtn
                  count={numSelectedFilters}
                  buttonSize="sm"
                  isActive={showPopover}
                  ref={licensorNameRef}
                  iconTrailing={IconChevronDown}
                >
                  {t(
                    'atlas.labels.licensors',
                    'Licensors',
                    'Label for Licensors',
                  )}
                </CounterBtn>
              </PopoverTrigger>
            )}
          </Container>
        </InputsContainer>
      </DescriptionAndInputsContainer>
      <UnclearedVideosTable
        licensors={
          selectedLicensors.length ? selectedLicensors : allUserLicensors
        }
        dateRange={dateRange}
        setTotal={setTotalNumber}
      />
    </>
  );
};
